/**
 * Inline Manual player integration (SPA / loader pattern).
 */
import { ensureInlineManualStyles } from '@plugins/inlineManual/styles';

import config from '@/config';

const INLINE_MANUAL_SCRIPT_ID = 'inline-manual-player-script';
const INLINE_MANUAL_SCRIPT_ATTR = 'data-inline-manual-loader';

let initialised = false;
let loadingPromise = null;
let lastBootSignature = null;
let shadowRootObserver = null;

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';
const toSignature = payload => JSON.stringify(payload || {});

const normaliseSiteId = siteId => {
    const value = siteId ?? config.INLINE_MANUAL_SITE_ID;
    return typeof value === 'string' ? value.trim() : '';
};

export const isInlineManualEnabled = ({ siteId } = {}) => Boolean(normaliseSiteId(siteId));

export const buildInlineManualPlayerOptions = ({ language } = {}) => {
    const code = typeof language === 'string' && language.trim() ? language.trim() : 'en';
    return { language: code };
};

const ensureQueueStub = () => {
    if (typeof window.InlineManual === 'function') {
        return;
    }

    window.IMCallbacks = window.IMCallbacks || {};

    const inlineManual = function () {
        (inlineManual.q = inlineManual.q || []).push(Array.from(arguments));
    };
    inlineManual.q = inlineManual.q || [];
    inlineManual.l = Number(new Date());
    inlineManual.snippet = { version: '2.0' };
    window.InlineManual = inlineManual;
};

const syncInlineManualShadowStyles = () => {
    if (!isBrowser()) {
        return;
    }

    ensureInlineManualStyles(document);

    const nodes = document.querySelectorAll('*');
    nodes.forEach(node => {
        if (node.shadowRoot) {
            ensureInlineManualStyles(node.shadowRoot);
        }
    });
};

const setupShadowRootObserver = () => {
    if (!isBrowser() || shadowRootObserver || typeof MutationObserver === 'undefined') {
        return;
    }

    shadowRootObserver = new MutationObserver(records => {
        records.forEach(record => {
            record.addedNodes.forEach(addedNode => {
                if (!addedNode || addedNode.nodeType !== 1) {
                    return;
                }

                if (addedNode.shadowRoot) {
                    ensureInlineManualStyles(addedNode.shadowRoot);
                }

                if (!addedNode.querySelectorAll) {
                    return;
                }

                const descendants = addedNode.querySelectorAll('*');
                descendants.forEach(descendant => {
                    if (descendant.shadowRoot) {
                        ensureInlineManualStyles(descendant.shadowRoot);
                    }
                });
            });
        });
    });

    shadowRootObserver.observe(document.body, { childList: true, subtree: true });
};

const injectPlayerScript = appKey => {
    const existing = document.getElementById(INLINE_MANUAL_SCRIPT_ID);
    if (existing) {
        return Promise.resolve();
    }

    const scriptSrc = `https://cdn.inlinemanual.com/inm/loader/loader.${appKey}.js`;
    const script = document.createElement('script');
    script.id = INLINE_MANUAL_SCRIPT_ID;
    script.src = scriptSrc;
    script.async = true;
    script.defer = true;
    script.setAttribute(INLINE_MANUAL_SCRIPT_ATTR, 'true');
    document.head.appendChild(script);

    return Promise.resolve();
};

export const initInlineManual = async ({
    siteId,
    userData = {},
    options = {},
    debug = false,
    autoBoot = true
} = {}) => {
    if (!isBrowser() || initialised || loadingPromise) {
        return loadingPromise || Promise.resolve();
    }

    const resolvedSiteId = normaliseSiteId(siteId);
    if (!resolvedSiteId) {
        return Promise.resolve();
    }

    window.inlinemanual_app_key = resolvedSiteId;
    window.inlineManualOptions = options;
    ensureQueueStub();

    if (debug) {
        window.inline_manual_debug = true;
    }

    if (autoBoot) {
        const initialBootPayload = { userData, options: window.inlineManualOptions || {} };
        lastBootSignature = toSignature(initialBootPayload);
        window.InlineManual('boot', userData, window.inlineManualOptions);
    }
    setupShadowRootObserver();
    syncInlineManualShadowStyles();

    loadingPromise = injectPlayerScript(resolvedSiteId).finally(() => {
        initialised = true;
    });

    return loadingPromise;
};

export const bootInlineManual = ({ userData = {}, options } = {}) => {
    if (!isBrowser() || typeof window.InlineManual !== 'function') {
        return;
    }

    if (options !== undefined) {
        window.inlineManualOptions = options;
    }

    const bootPayload = { userData, options: window.inlineManualOptions || {} };
    const signature = toSignature(bootPayload);
    if (signature === lastBootSignature) {
        return;
    }

    lastBootSignature = signature;
    window.InlineManual('boot', userData, window.inlineManualOptions || {});
    syncInlineManualShadowStyles();
};

/**
 * Notify the player of SPA / profile changes.
 * Per Inline Manual (widget v2): calling `update` with a payload that omits `uid` can be
 * merged as player *config*; include `uid` when passing route or other user segmentation fields.
 * A bare `update()` (no second argument) triggers a profile refresh and relies on the real URL.
 */
export const updateInlineManual = data => {
    if (!isBrowser() || typeof window.InlineManual !== 'function') {
        return;
    }

    if (data === undefined || data === null) {
        window.InlineManual('update');
        syncInlineManualShadowStyles();
        return;
    }

    window.InlineManual('update', data);
    syncInlineManualShadowStyles();
};

export const callInlineManual = (method, ...args) => {
    if (!isBrowser() || typeof window.InlineManual !== 'function' || !method) {
        return;
    }

    window.InlineManual(method, ...args);
};

export const showInlineManualWidget = () => callInlineManual('showWidget');
export const hideInlineManualWidget = () => callInlineManual('hideWidget');
export const launchInlineManualTopic = topicId => callInlineManual('showTopic', topicId);

export const attachInlineManualRouterSync = ({ router, getAuth, getOptions } = {}) => {
    if (!router || typeof router.afterEach !== 'function' || typeof getAuth !== 'function') {
        return;
    }

    let hadAuthenticatedUser = false;

    router.afterEach(to => {
        const auth = getAuth();

        if (!(auth && auth.has_user)) {
            hadAuthenticatedUser = false;
            return;
        }

        const userData = {
            uid: String(auth.user_id),
            created: auth.user_created_at,
            email: auth.user_email,
            name: auth.user_name,
            roles: auth.organisation_roles,
            plan: auth.organisation_plan,
            group: auth.organisation_type,
            organisation_id: auth.organisation_id,
            account_id: auth.account_id,
            unread_notifications_count: auth.organisation_unread_notifications_count
        };
        const options = typeof getOptions === 'function' ? getOptions() : {};

        // Boot once per authentication (after login or full refresh). Route changes
        // should use `update` only; repeated `boot` calls can duplicate player state.
        if (!hadAuthenticatedUser) {
            hadAuthenticatedUser = true;
            bootInlineManual({ userData, options });
            return;
        }

        updateInlineManual({
            uid: userData.uid,
            route_name: to.name || null,
            route_path: to.path || window.location.pathname,
            route_query: window.location.search || ''
        });
    });
};

export const shutdownInlineManual = () => {
    if (!isBrowser()) {
        return;
    }

    if (typeof window.InlineManual === 'function') {
        window.InlineManual('shutdown');
    }
    if (shadowRootObserver) {
        shadowRootObserver.disconnect();
        shadowRootObserver = null;
    }
    initialised = false;
    loadingPromise = null;
    lastBootSignature = null;
    delete window.inlinemanual_app_key;
    delete window.inlineManualOptions;
    delete window.inline_manual_debug;
};

export default {
    buildInlineManualPlayerOptions,
    callInlineManual,
    bootInlineManual,
    hideInlineManualWidget,
    initInlineManual,
    isInlineManualEnabled,
    launchInlineManualTopic,
    attachInlineManualRouterSync,
    showInlineManualWidget,
    shutdownInlineManual,
    updateInlineManual
};
